import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { FaRegTrashAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';

import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  DatepickerV2,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  toCurrencyString,
  toPercent,
} from '@client/toolkit';
import type { CurrencyCode } from '@client/toolkit';

import {
  ValuationFrequency,
  ValuationMethod,
  OwnershipShareItem,
  PermissionKeys,
  useGetSpacesByIdPortfolioFinancialsQuery,
  usePutSpacesByIdPortfolioFinancialsMutation,
  usePostSpacesByIdPortfolioFinancialsValuationReportsMutation,
  useLazyGetSpacesByIdPortfolioFinancialsValuationReportsAndReportIdQuery,
  useDeleteSpacesByIdPortfolioFinancialsValuationReportsAndReportIdMutation,
} from '@client/api';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';

export interface ValuationCardProps {
  buildingId?: string;
}

const VALUATION_FREQUENCY_VALUES: ValuationFrequency[] = ['SemiAnnual', 'Annual', 'Biennial'];

const VALUATION_METHOD_VALUES: ValuationMethod[] = [
  'Yield',
  'Dcf',
  'MarketComparable',
  'Residual',
  'Cost',
  'AssetValue',
  'Combination',
  'Other',
];

export function ValuationCard({ buildingId }: ValuationCardProps) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('portfolio');

  const { data: financials, isLoading } = useGetSpacesByIdPortfolioFinancialsQuery(
    { id: buildingId ?? '' },
    { skip: !buildingId },
  );

  const [upsert, { isLoading: isSaving }] = usePutSpacesByIdPortfolioFinancialsMutation();
  const [uploadReport, { isLoading: isUploading }] = usePostSpacesByIdPortfolioFinancialsValuationReportsMutation();
  const [downloadReport] = useLazyGetSpacesByIdPortfolioFinancialsValuationReportsAndReportIdQuery();
  const [deleteReport] = useDeleteSpacesByIdPortfolioFinancialsValuationReportsAndReportIdMutation();
  const { hasAnyPermission } = useSpacePermissions({ id: buildingId });
  const canEdit = hasAnyPermission([PermissionKeys.Space.Update]);

  const [editing, setEditing] = useState(false);
  const [propertyValue, setPropertyValue] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState<Dayjs | null>(null);
  const [valuationFrequency, setValuationFrequency] = useState<ValuationFrequency | ''>('');
  const [valuationMethod, setValuationMethod] = useState<ValuationMethod | ''>('');
  const [exitYieldAssumption, setExitYieldAssumption] = useState('');
  const [valuatorFirm, setValuatorFirm] = useState('');
  const [ownershipShares, setOwnershipShares] = useState<OwnershipShareItem[]>([]);

  useEffect(() => {
    if (financials) {
      setPropertyValue(financials.propertyValue?.toString() ?? '');
      setPurchasePrice(financials.purchasePrice?.toString() ?? '');
      setPurchaseDate(financials.purchaseDate ? dayjs(financials.purchaseDate) : null);
      setValuationFrequency(financials.valuationFrequency ?? '');
      setValuationMethod(financials.valuationMethod ?? '');
      setExitYieldAssumption(financials.exitYieldAssumption?.toString() ?? '');
      setValuatorFirm(financials.valuatorFirm ?? '');
      setOwnershipShares(financials.ownershipShares?.map((o) => ({ tenantId: o.tenantId, share: o.share ?? 0 })) ?? []);
    }
  }, [financials]);

  const handleEdit = () => setEditing(true);

  const handleCancel = () => {
    if (financials) {
      setPropertyValue(financials.propertyValue?.toString() ?? '');
      setPurchasePrice(financials.purchasePrice?.toString() ?? '');
      setPurchaseDate(financials.purchaseDate ? dayjs(financials.purchaseDate) : null);
      setValuationFrequency(financials.valuationFrequency ?? '');
      setValuationMethod(financials.valuationMethod ?? '');
      setExitYieldAssumption(financials.exitYieldAssumption?.toString() ?? '');
      setValuatorFirm(financials.valuatorFirm ?? '');
      setOwnershipShares(financials.ownershipShares?.map((o) => ({ tenantId: o.tenantId, share: o.share ?? 0 })) ?? []);
    }
    setEditing(false);
  };

  const handleSave = async () => {
    if (!buildingId) return;
    try {
      await upsert({
        id: buildingId,
        body: {
          propertyValue: propertyValue ? parseFloat(propertyValue) : null,
          purchasePrice: purchasePrice ? parseFloat(purchasePrice) : null,
          purchaseDate: purchaseDate?.format('YYYY-MM-DD') ?? null,
          valuationFrequency: valuationFrequency || null,
          valuationMethod: valuationMethod || null,
          exitYieldAssumption: exitYieldAssumption ? parseFloat(exitYieldAssumption) : null,
          valuatorFirm: valuatorFirm || null,
          ownershipShares: ownershipShares.length > 0 ? ownershipShares : null,
        },
      }).unwrap();
      setEditing(false);
    } catch {
      // error handled by RTK
    }
  };

  // Ownership shares are stored as a fraction (1 = 100%), matching the Matrikkel-derived
  // TenantSpace.OwnershipShare. The input is entered as a percent, so convert percent -> fraction.
  const handleOwnershipShareChange = (tenantId: string, value: string) => {
    setOwnershipShares((prev) =>
      prev.map((s) => (s.tenantId === tenantId ? { ...s, share: value ? parseFloat(value) / 100 : 0 } : s)),
    );
  };

  const shareInputValue = (tenantId: string) => {
    const current = ownershipShares.find((s) => s.tenantId === tenantId)?.share;
    return current != null ? `${current * 100}` : '';
  };

  /* File upload */
  const inputRef = useRef<HTMLInputElement>(null);
  const handleClickUpload = useCallback(() => inputRef.current?.click(), []);
  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!buildingId) return;
      const file = event.target.files?.[0];
      if (!file) return;

      await uploadReport({ id: buildingId, body: { File: file } });

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [buildingId, uploadReport],
  );

  const handleDownload = useCallback(
    async (reportId: string, fileName: string) => {
      if (!buildingId) return;
      try {
        const { data } = await downloadReport({ id: buildingId, reportId });
        if ((data as unknown) instanceof Blob) {
          const blobUrl = window.URL.createObjectURL(data as unknown as Blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }
      } catch {
        toast.error(t('valuation.downloadFailed'));
      }
    },
    [buildingId, downloadReport],
  );

  const handleDeleteReport = useCallback(
    async (reportId: string) => {
      if (!buildingId) return;
      await deleteReport({ id: buildingId, reportId });
    },
    [buildingId, deleteReport],
  );

  const currency = (financials?.currency ?? 'NOK') as CurrencyCode;

  const formatAmount = (val?: number | null) => toCurrencyString(val ?? undefined, '\u2013', { currency });

  const formatDate = (val?: string | null) => {
    if (!val) return '\u2013';
    const parts = val.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return val;
  };

  const formatPercent = (val?: number | null) => {
    if (val == null) return '\u2013';
    return toPercent(val / 100, '\u2013');
  };

  // Ownership shares are stored as a fraction (1 = 100%), so format them directly.
  const formatShare = (val?: number | null) => {
    if (val == null) return '\u2013';
    return toPercent(val, '\u2013');
  };

  const frequencyLabel = (val?: ValuationFrequency | null) => (val ? t(`valuation.frequency.${val}`) : '\u2013');

  const methodLabel = (val?: ValuationMethod | null) => (val ? t(`valuation.method.${val}`) : '\u2013');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" variant="highlighted">
          {t('valuation.title')}
        </CardTitle>
        <CardActions>
          {!editing ? (
            canEdit && (
              <Button size="sm" onClick={handleEdit}>
                {tCommon('edit')}
              </Button>
            )
          ) : (
            <>
              <Button size="sm" color="primary-dark" onClick={handleCancel} disabled={isSaving}>
                {tCommon('cancel')}
              </Button>
              <Button size="sm" color="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? t('saving') : tCommon('save')}
              </Button>
            </>
          )}
        </CardActions>
      </CardHeader>

      <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
        <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{t('valuation.type')}</TableHeadCell>
              <TableHeadCell align="right">{t('valuation.value')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>

          <TableBody>
            {isLoading &&
              Array.from(Array(6).keys()).map((x) => (
                <TableRow key={x}>
                  <TableCell loading />
                  <TableCell loading />
                </TableRow>
              ))}

            {!isLoading && (
              <>
                {/* Estimert markedsverdi */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.propertyValue')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <Input
                        type="number"
                        size="medium"
                        value={propertyValue}
                        onChange={(e) => setPropertyValue(e.target.value)}
                        placeholder="0"
                        className="dark:bg-surface-base"
                      />
                    ) : (
                      formatAmount(financials?.propertyValue)
                    )}
                  </TableCell>
                </TableRow>

                {/* Anskaffelsesverdi */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.purchasePrice')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <Input
                        type="number"
                        size="medium"
                        value={purchasePrice}
                        onChange={(e) => setPurchasePrice(e.target.value)}
                        placeholder="0"
                        className="dark:bg-surface-base"
                      />
                    ) : (
                      formatAmount(financials?.purchasePrice)
                    )}
                  </TableCell>
                </TableRow>

                {/* Anskaffelsesdato */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.purchaseDate')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <DatepickerV2
                        value={purchaseDate ?? undefined}
                        onChange={(date) => setPurchaseDate(date ?? null)}
                        className="w-full"
                      />
                    ) : (
                      formatDate(financials?.purchaseDate)
                    )}
                  </TableCell>
                </TableRow>

                {/* Vurderingsfrekvens */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.valuationFrequency')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <Select
                        size="medium"
                        value={valuationFrequency}
                        onChange={(e) => setValuationFrequency(e.target.value as ValuationFrequency | '')}
                      >
                        <option value="">{t('valuation.selectPlaceholder')}</option>
                        {VALUATION_FREQUENCY_VALUES.map((v) => (
                          <option key={v} value={v}>
                            {t(`valuation.frequency.${v}`)}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      frequencyLabel(financials?.valuationFrequency)
                    )}
                  </TableCell>
                </TableRow>

                {/* Verdsettelsesmetode */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.valuationMethod')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <Select
                        size="medium"
                        value={valuationMethod}
                        onChange={(e) => setValuationMethod(e.target.value as ValuationMethod | '')}
                      >
                        <option value="">{t('valuation.selectPlaceholder')}</option>
                        {VALUATION_METHOD_VALUES.map((v) => (
                          <option key={v} value={v}>
                            {t(`valuation.method.${v}`)}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      methodLabel(financials?.valuationMethod)
                    )}
                  </TableCell>
                </TableRow>

                {/* Yield-forutsetning (EXIT) */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.exitYieldAssumption')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <Input
                        type="number"
                        size="medium"
                        value={exitYieldAssumption}
                        onChange={(e) => setExitYieldAssumption(e.target.value)}
                        placeholder="0"
                        className="dark:bg-surface-base"
                      />
                    ) : (
                      formatPercent(financials?.exitYieldAssumption)
                    )}
                  </TableCell>
                </TableRow>

                {/* Verdivurderer (firma) */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.valuatorFirm')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <Input
                        type="text"
                        size="medium"
                        value={valuatorFirm}
                        onChange={(e) => setValuatorFirm(e.target.value)}
                        placeholder={t('valuation.firmPlaceholder')}
                        className="dark:bg-surface-base"
                      />
                    ) : (
                      financials?.valuatorFirm || '\u2013'
                    )}
                  </TableCell>
                </TableRow>

                {/* Eierandeler */}
                {financials?.ownershipShares?.map((owner) => (
                  <TableRow key={owner.tenantId} verticalAlign="middle">
                    <TableCell>
                      <strong>
                        {t('valuation.fields.ownershipShare')} {'\u2013'} {owner.name}
                      </strong>
                    </TableCell>
                    <TableCell align="right">
                      {editing ? (
                        <Input
                          type="number"
                          size="medium"
                          value={shareInputValue(owner.tenantId)}
                          onChange={(e) => handleOwnershipShareChange(owner.tenantId, e.target.value)}
                          placeholder="0"
                          className="dark:bg-surface-base"
                        />
                      ) : (
                        formatShare(owner.share)
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Verdsettelsesrapporter */}
                <TableRow verticalAlign="middle">
                  <TableCell>
                    <strong>{t('valuation.fields.valuationReport')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <div className="flex flex-col items-end gap-2">
                      {financials?.valuationReports?.map((report) => (
                        <div key={report.id} className="flex items-center gap-2">
                          <span
                            className="cursor-pointer underline truncate max-w-[200px]"
                            onClick={() => handleDownload(report.id, report.fileName)}
                          >
                            {report.fileName}
                          </span>
                          {canEdit && (
                            <Button
                              className="text-brand-primary bg-surface-elevated size-7"
                              size="xs"
                              color="custom"
                              onClick={() => handleDeleteReport(report.id)}
                            >
                              <FaRegTrashAlt />
                            </Button>
                          )}
                        </div>
                      ))}
                      {canEdit && (
                        <Button
                          size="sm"
                          color="primary"
                          onClick={handleClickUpload}
                          disabled={isUploading}
                          progressing={isUploading}
                        >
                          <input
                            ref={inputRef}
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={handleFileChange}
                          />
                          {tCommon('upload')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
